import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Instagram } from "lucide-react";
import api from "@/lib/api";
import { NYE_EVENT } from "@/data/nye";

// Home hero backdrop: the "Women Only" flyer (microphone + gold text) that
// signals the queer women-only New Year's Eve event up front.
const HERO_IMG =
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_elevated-collective/artifacts/17730a141b68ef07_Black%20and%20Yellow%20Jazz%20Concert%20Instagram%20Post%20-%204.webp";

const PILLARS = [
    { n: "01", t: "Curated experiences", d: "Long-table suppers, rooftop socials, weekend residencies — designed for the room, not the room count." },
    { n: "02", t: "Authentic representation", d: "Lesbian visibility with warmth and refinement — a community for lesbian women of every background." },
    { n: "03", t: "Original storytelling", d: "A new series centered on everyday lesbian women — married, dating, and single — told with real intimacy." },
    { n: "04", t: "Intentional connection", d: "Small rooms. Considered menus. Conversations that don't rush toward small talk." },
];

export default function Home() {
    const [events, setEvents] = useState([]);
    useEffect(() => {
        api.get("/events").then((r) => {
            const now = new Date();
            const upcoming = (r.data || []).filter((ev) => !ev.date_iso || new Date(ev.date_iso) >= now);
            setEvents(upcoming.slice(0, 3));
        }).catch(() => {});
    }, []);

    return (
        <main data-testid="home-page" className="pt-0">
            {/* HERO */}
            <section className="relative min-h-[100vh] w-full grain">
                <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 hero-mask" />
                <div className="relative z-10 mx-auto max-w-[1600px] px-6 lg:px-12 pt-40 lg:pt-56 pb-24 min-h-[100vh] flex flex-col justify-end">
                    <p className="eyebrow" data-testid="hero-eyebrow">Mrs.Black(In)Love</p>
                    <h1
                        className="font-display text-[color:var(--soft-cream)] mt-6 max-w-5xl"
                        style={{ fontSize: "clamp(3rem, 9vw, 8.5rem)" }}
                        data-testid="hero-headline"
                    >
                        Community is the
                        <br />
                        <em className="text-[color:var(--gold)]">new luxury.</em>
                    </h1>
                    <p className="mt-8 max-w-xl text-[color:var(--cream-muted)] text-lg leading-relaxed">
                        A community and lifestyle platform for lesbian women — with curated experiences, authentic representation, and an original series about the way we actually love.
                    </p>
                    <div className="mt-12 flex flex-wrap gap-4">
                        <Link
                            to={`/experiences#${NYE_EVENT.slug}`}
                            className="btn-gold btn-gold-solid"
                            data-testid="hero-cta-nye-event"
                        >
                            A Queer New Year's Celebration <ArrowRight size={14} strokeWidth={1.5} />
                        </Link>
                        <Link to="/original-series" className="btn-gold" data-testid="hero-cta-series-learn">
                            Learn more about our reality series
                        </Link>
                    </div>
                </div>
            </section>

            {/* NEW YEAR'S TEASER — "A Jazz Affair" */}
            <section className="relative py-28 border-t border-[color:var(--espresso)] bg-[color:var(--warm-black)] overflow-hidden" data-testid="nye-teaser">
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.07] pointer-events-none"
                    style={{ backgroundImage: "radial-gradient(circle at 20% 10%, var(--gold), transparent 45%), radial-gradient(circle at 85% 90%, var(--wine), transparent 45%)" }}
                />
                <div className="relative mx-auto max-w-[1600px] px-6 lg:px-12 grid lg:grid-cols-12 gap-14 items-center">
                    <div className="lg:col-span-7 relative" data-testid="nye-media">
                        <div className="aspect-[16/10] relative overflow-hidden border border-[color:var(--gold)]/30 bg-[color:var(--dark-grey)]">
                            {NYE_EVENT.video_url ? (
                                <video
                                    src={NYE_EVENT.video_url}
                                    poster={NYE_EVENT.hero_image}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    className="absolute inset-0 h-full w-full object-cover"
                                    data-testid="nye-video"
                                />
                            ) : (
                                <img
                                    src={NYE_EVENT.hero_image}
                                    alt="A Jazz Affair — Queer New Year's Celebration"
                                    className="absolute inset-0 h-full w-full object-contain bg-black"
                                    data-testid="nye-poster"
                                />
                            )}
                        </div>
                        {NYE_EVENT.instagram_reel_url && (
                            <a
                                href={NYE_EVENT.instagram_reel_url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-4 inline-flex items-center justify-center gap-2 text-xs tracking-[0.24em] uppercase text-[color:var(--gold)] border border-[color:var(--gold)]/50 hover:border-[color:var(--gold)] hover:bg-[color:var(--gold)]/5 transition-colors duration-300 px-5 py-3 w-full"
                                data-testid="nye-instagram-reel-home"
                            >
                                <Instagram size={14} strokeWidth={1.5} /> Watch the full version on Instagram
                            </a>
                        )}
                    </div>
                    <div className="lg:col-span-5" data-testid="nye-copy">
                        <p className="eyebrow">{NYE_EVENT.date_label} · {NYE_EVENT.venue}</p>
                        <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.95]">
                            Start 2027 in a room <em className="text-[color:var(--gold)]">made for us</em>.
                        </h2>
                        <p className="mt-6 text-[color:var(--cream-muted)] text-lg leading-relaxed">
                            A rooftop jazz lounge experience for queer women — live all-female jazz band, a female DJ, open-mic poetry, and skyline views. {NYE_EVENT.price_advance}.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to={`/experiences#${NYE_EVENT.slug}`} className="btn-gold" data-testid="nye-learn-more">
                                Learn more
                            </Link>
                            <a
                                href={NYE_EVENT.ticket_url}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-gold btn-gold-solid"
                                data-testid="nye-purchase"
                            >
                                Purchase your ticket now <ArrowRight size={14} strokeWidth={1.5} />
                            </a>
                        </div>
                        <p className="mt-6 text-xs text-[color:var(--cream-muted)]">
                            {NYE_EVENT.time_label} · Intentional queer women-only event. No ticket, no entry.
                        </p>
                    </div>
                </div>
            </section>

            {/* GUEST SPOTLIGHT — hidden for now (admin panel + apply flow remain available) */}

            {/* MANIFESTO */}
            <section className="bg-[color:var(--warm-black)] py-32 border-t border-[color:var(--espresso)]">
                <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-4">
                        <p className="eyebrow">Manifesto</p>
                        <div className="hairline mt-6" />
                    </div>
                    <div className="lg:col-span-8">
                        <p className="font-serif text-3xl md:text-5xl leading-tight text-[color:var(--soft-cream)]">
                            We build rooms where <em className="text-[color:var(--gold)]">lesbian women</em> arrive as themselves — where the table is long, the lighting is warm, and everyone is welcome the moment they walk in.
                        </p>
                        <div className="mt-14 grid md:grid-cols-2 gap-x-14 gap-y-12">
                            {PILLARS.map((p) => (
                                <div key={p.n}>
                                    <span className="font-serif italic text-[color:var(--gold)] text-lg">{p.n}</span>
                                    <h3 className="font-serif text-2xl mt-2">{p.t}</h3>
                                    <p className="mt-3 text-[color:var(--cream-muted)] text-sm leading-relaxed">{p.d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* UPCOMING EXPERIENCES */}
            <section className="bg-[color:var(--warm-black)] py-32 border-t border-[color:var(--espresso)]">
                <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
                        <div>
                            <p className="eyebrow">Upcoming Experiences</p>
                            <h2 className="font-display text-5xl md:text-7xl mt-4">A season of small rooms.</h2>
                        </div>
                        <Link to="/experiences" className="link-underline text-sm uppercase tracking-[0.24em]" data-testid="home-view-all-experiences">
                            View all →
                        </Link>
                    </div>

                    <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                        {events.map((e, i) => (
                            <Link
                                key={e.id}
                                to={`/experiences/${e.slug}`}
                                data-testid={`home-event-card-${i}`}
                                className="group zoom-parent block"
                            >
                                <div className="overflow-hidden">
                                    <img src={e.hero_image} alt={e.title} className="h-[420px] w-full object-cover" />
                                </div>
                                <div className="mt-6">
                                    <p className="text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]">
                                        {e.city}{e.is_coming_soon ? " · Coming soon" : ""}
                                    </p>
                                    <h3 className="font-serif text-3xl mt-2 group-hover:text-[color:var(--gold)] transition-colors duration-300">
                                        {e.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-[color:var(--cream-muted)]">{e.tagline}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FROM THE ROOM — real event photography strip */}
            <section className="bg-[color:var(--dark-grey)] py-32 border-t border-[color:var(--espresso)]" data-testid="from-the-room">
                <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
                        <div>
                            <p className="eyebrow">From the room</p>
                            <h2 className="font-display text-5xl md:text-7xl mt-4 max-w-3xl">Moments from <em className="text-[color:var(--gold)]">Sunset in Tuscany</em>.</h2>
                        </div>
                        <Link to="/experiences/sunset-in-tuscany" className="link-underline text-sm uppercase tracking-[0.24em]" data-testid="from-the-room-view">See the evening →</Link>
                    </div>
                    <div className="mt-16 grid md:grid-cols-3 gap-6">
                        {[
                            "https://customer-assets-lxgj4vgw.emergentagent.net/job_elevated-collective/artifacts/g5l8wf4h_2F80957B-0447-4EB8-B799-9DD1CF0359C3.png",
                            "https://customer-assets-lxgj4vgw.emergentagent.net/job_elevated-collective/artifacts/bc5dobxc_6E43E8A0-7F56-4688-8221-10487C09C7CA.png",
                            "https://customer-assets-lxgj4vgw.emergentagent.net/job_elevated-collective/artifacts/oye26yk2_6B118AC8-7997-4A4B-A35F-ABA620CFB5D5.png",
                        ].map((src, i) => (
                            <div key={src} className="zoom-parent overflow-hidden">
                                <img src={src} alt={`Sunset in Tuscany guest ${i + 1}`} className="w-full h-[520px] object-cover" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED SPOTLIGHT — Faith Quinn feature retired. Section now serves
                as an always-on call-to-apply. When admin publishes the next feature
                via the admin panel, restore the dynamic render below. */}
            <section className="bg-[color:var(--warm-black)] border-t border-[color:var(--espresso)] py-24" data-testid="featured-this-week">
                <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
                    <div className="flex items-baseline gap-6">
                        <p className="eyebrow">Featured This Week</p>
                        <div className="hairline" />
                    </div>
                    <div className="mt-10 grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7">
                            <h3 className="font-display text-5xl md:text-7xl leading-[0.95]">Your business, <em className="text-[color:var(--gold)]">right here</em>.</h3>
                            <p className="mt-6 text-[color:var(--cream-muted)] text-lg max-w-2xl leading-relaxed">
                                Every week we spotlight a business, product, or service loved by our community — free, always. Queer-owned, Black-owned, minority-owned, and women-owned businesses are prioritized.
                            </p>
                            <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-[color:var(--soft-cream)] font-serif" data-testid="feature-ad-benefits">
                                <li className="border-b border-[color:var(--espresso)] pb-2">— A full write-up + photos</li>
                                <li className="border-b border-[color:var(--espresso)] pb-2">— Homepage + socials placement</li>
                                <li className="border-b border-[color:var(--espresso)] pb-2">— Direct link to your site & IG</li>
                                <li className="border-b border-[color:var(--espresso)] pb-2">— Permanent archive listing</li>
                            </ul>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link to="/feature-your-business" className="btn-gold btn-gold-solid" data-testid="apply-to-be-featured">
                                    Apply to be featured
                                </Link>
                                <Link to="/featured" className="btn-gold" data-testid="view-featured-archive">
                                    Browse the archive
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="border border-[color:var(--gold)]/30 aspect-[4/5] flex flex-col items-center justify-center p-10 relative overflow-hidden bg-[color:var(--dark-grey)]/40" data-testid="feature-ad-placeholder">
                                <div className="absolute inset-4 border border-[color:var(--gold)]/15" />
                                <p className="text-[0.65rem] uppercase tracking-[0.28em] text-[color:var(--gold)]">This spot</p>
                                <p className="mt-6 font-display text-5xl text-center text-[color:var(--soft-cream)] leading-[0.95]">Could be<br /><em className="text-[color:var(--gold)]">yours</em>.</p>
                                <p className="mt-6 text-xs text-[color:var(--cream-muted)] text-center max-w-[220px]">Free to apply. Reviewed weekly by the Mrs.Black(In)Love editors.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERIES TEASE */}
            <section className="relative py-40 border-t border-[color:var(--espresso)] overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1758738180478-0fd260c86605?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxibGFjayUyMHdvbWFuJTIwcG9ydHJhaXQlMjBlbGVnYW50JTIwZ29sZHxlbnwwfHx8fDE3ODU2ODY4ODl8MA&ixlib=rb-4.1.0&q=85"
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover opacity-45"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--warm-black)] via-[color:var(--warm-black)]/60 to-transparent" />
                <div className="relative mx-auto max-w-[1600px] px-6 lg:px-12">
                    <p className="eyebrow" data-testid="home-series-eyebrow">Original Series — Cast Finalized · Filming October</p>
                    <h2 className="font-display text-5xl md:text-8xl mt-4 max-w-4xl leading-[0.95]">
                        A series that shows the way we <em className="text-[color:var(--gold)]">actually love</em>.
                    </h2>
                    <p className="mt-8 max-w-xl text-[color:var(--cream-muted)] text-lg">
                        An unscripted, cinematic series centered on everyday lesbian women — married, dating, or single. Fourteen women selected. One house, secured. Filming begins this October. Stay tuned.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <Link to="/original-series#trailer" className="btn-gold btn-gold-solid" data-testid="home-series-trailer">
                            ▶ Watch the trailer
                        </Link>
                        <Link to="/original-series" className="btn-gold" data-testid="home-series-learn-more">
                            About the series
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
